<div class="impact-dashboard-with-winners-container">
  <!-- Hero Section -->
  <div class="hero-section">
    <div class="hero-content">
      <h1>Hey {{ userName }}, welcome to the PrettyDamnQuick Impact Dashboard</h1>
      <p>
        You have achieved incredible things with PrettyDamnQuick but there is still much more to unlock. Let's keep
        increasing your profit!
      </p>
    </div>
  </div>

  <!-- Overview Section -->
  <div class="overview-section">
    <div class="overview-header">
      <div class="section-title-area">
        <div class="section-title-row">
          <h2>PrettyDamnQuick Checkout Optimization Impact Overview</h2>
          @if (errorOverviewMetrics) {
            <button class="refresh-btn" (click)="onRefreshOverviewMetrics()" title="Refresh">
              <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
            </button>
          }
        </div>
        <p>Your complete value summary from checkout optimizations</p>
      </div>
    </div>

    <!-- Metrics Header Component -->
    <impact-metrics-header
      [metrics]="overviewMetrics"
      [loading]="loadingOverviewMetrics"
      [error]="errorOverviewMetrics"
      [showRoi]="!hideRoi"
      [showImplementedComponents]="true"
      (refresh)="onRefreshOverviewMetrics()">
    </impact-metrics-header>
  </div>

  <!-- Revenue Uplift Progress Chart Section -->
  @if (hasRevenueUpliftData || loadingRevenueUpliftChart) {
    <div class="revenue-uplift-chart-section">
      <div class="section-header">
        <div class="section-title-area">
          <div class="section-title-row">
            <h2>Revenue Uplift Progress</h2>
            @if (errorRevenueUpliftChart) {
              <button class="refresh-btn" (click)="onRefreshRevenueUpliftChart()" title="Refresh">
                <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
              </button>
            }
          </div>
          <p>Cumulative annualized revenue impact from implemented A/B test wins</p>
        </div>
      </div>

      <div class="chart-card">
        <revenue-uplift-progress-chart
          [dataPoints]="revenueUpliftDataPoints"
          [currencySymbol]="currencySymbol"
          [loading]="loadingRevenueUpliftChart"
          [error]="errorRevenueUpliftChart">
        </revenue-uplift-progress-chart>
      </div>
    </div>
  }

  <!-- Direct Revenue Section -->
  <div class="direct-revenue-section">
    <div class="section-header">
      <div class="section-title-area">
        <div class="section-title-row">
          <h2>Direct Revenue</h2>
          @if (errorDirectRevenueMetrics) {
            <button class="refresh-btn" (click)="onRefreshDirectRevenueMetrics()" title="Refresh">
              <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
            </button>
          }
        </div>
        <p>Revenue streams powered by PrettyDamnQuick optimization features</p>
      </div>
      <div class="section-actions">
        <pdq-date-range-picker
          #directRevenueDatePicker
          (onApply)="onDirectRevenueDateChange($event)"
          [createdAt]="shopCreated()"
          [defaultPreset]="defaultRange"
          petitionType="range"
          positionRight="true"
          background="light"></pdq-date-range-picker>
      </div>
    </div>

    <!-- Revenue Cards Row -->
    <div class="revenue-cards-row">
      @for (card of sortedDirectRevenueCards; track card.id) {
        <div class="metric-card feature-card" [class.configured]="directRevenueMetrics[card.hasRevenueKey]">
          <div class="metric-icon feature-icon" [ngClass]="card.cardIconClass">
            <i class="icon" [ngClass]="card.iconClass"></i>
          </div>
          <p class="feature-label">{{ card.label }}</p>
          @if (loadingDirectRevenueMetrics) {
            <p class="feature-sublabel">{{ card.sublabel }}</p>
            <div class="metric-skeleton metric-skeleton--value"></div>
          } @else if (directRevenueMetrics[card.hasRevenueKey]) {
            <p class="feature-sublabel">{{ card.sublabel }}</p>
            <p class="metric-value feature-value">{{ directRevenueMetrics[card.revenueKey] }}</p>
          } @else {
            <button class="configure-btn" (click)="onConfigureFeature(card.configureAction)">+ Configure</button>
          }
        </div>
      }
    </div>
  </div>

  <!-- Powered By PrettyDamnQuick Section -->
  <div class="powered-by-section">
    <div class="section-header">
      <div class="section-title-area">
        <div class="section-title-row">
          <h2>Powered By PrettyDamnQuick</h2>
          @if (errorPoweredByPdqMetrics) {
            <button class="refresh-btn" (click)="onRefreshPoweredByPdqMetrics()" title="Refresh">
              <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
            </button>
          }
        </div>
        <p>Real-time performance metrics from your PrettyDamnQuick integration</p>
      </div>
    </div>

    <!-- Performance Metrics Row -->
    <div class="performance-metrics-row">
      <div
        class="metric-card performance-card"
        matTooltip="Total checkout sessions where the rates were responded by PrettyDamnQuick."
        matTooltipClass="pdq-mat-tooltip">
        @if (loadingPoweredByPdqMetrics) {
          <div class="metric-skeleton metric-skeleton--icon"></div>
          <div class="metric-skeleton metric-skeleton--label"></div>
          <div class="metric-skeleton metric-skeleton--value"></div>
        } @else {
          <div class="metric-icon performance-icon checkouts-icon">
            <i class="icon icon-cp"></i>
          </div>
          <p class="metric-label">Checkouts Powered</p>
          <p class="metric-value performance-value">{{ poweredByPdqMetrics.checkoutsPowered }}</p>
        }
      </div>

      <div
        class="metric-card performance-card"
        matTooltip="Average time for PrettyDamnQuick to process and respond to checkout requests (last 7 days)."
        matTooltipClass="pdq-mat-tooltip">
        @if (loadingAvgResponseTime) {
          <div class="metric-skeleton metric-skeleton--icon"></div>
          <div class="metric-skeleton metric-skeleton--label"></div>
          <div class="metric-skeleton metric-skeleton--value"></div>
        } @else if (errorAvgResponseTime) {
          <div class="metric-icon performance-icon response-time-icon">
            <i class="icon icon-art"></i>
          </div>
          <p class="metric-label">Avg Response Time</p>
          <span class="metric-period">Last 7 days</span>
          <p class="metric-value performance-value">--</p>
          <button class="refresh-btn" (click)="onRefreshAvgResponseTime(); $event.stopPropagation()" title="Refresh">
            <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
          </button>
        } @else {
          <div class="metric-icon performance-icon response-time-icon">
            <i class="icon icon-art"></i>
          </div>
          <p class="metric-label">Avg Response Time</p>
          <span class="metric-period">Last 7 days</span>
          <p class="metric-value performance-value">{{ poweredByPdqMetrics.avgResponseTime }}</p>
        }
      </div>

      <div
        class="metric-card performance-card clickable"
        matTooltip="Total number of triggers successfully sent through all active PrettyDamnQuick integrations (last 7 days)."
        matTooltipClass="pdq-mat-tooltip"
        (click)="onNavigateToIntegrations()">
        @if (loadingIntegrationTriggers) {
          <div class="metric-skeleton metric-skeleton--icon"></div>
          <div class="metric-skeleton metric-skeleton--label"></div>
          <div class="metric-skeleton metric-skeleton--value"></div>
        } @else if (errorIntegrationTriggers) {
          <div class="metric-icon performance-icon integration-icon">
            <i class="icon icon-it"></i>
          </div>
          <p class="metric-label">Integration Triggers</p>
          <span class="metric-period">Last 7 days</span>
          <p class="metric-value performance-value">--</p>
          <button
            class="refresh-btn"
            (click)="onRefreshIntegrationTriggers(); $event.stopPropagation()"
            title="Refresh">
            <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
          </button>
        } @else {
          <div class="metric-icon performance-icon integration-icon">
            <i class="icon icon-it"></i>
          </div>
          <p class="metric-label">Integration Triggers</p>
          <span class="metric-period">Last 7 days</span>
          <p class="metric-value performance-value">{{ poweredByPdqMetrics.integrationTriggers }}</p>
          @if (poweredByPdqMetrics.connectedIntegrations.length > 0) {
            <div class="connected-integrations-badges">
              @for (integration of poweredByPdqMetrics.connectedIntegrations; track integration) {
                <span class="integration-badge">{{ integration }}</span>
              }
            </div>
          }
        }
      </div>

      <div
        class="metric-card performance-card"
        matTooltip="Total visits to your branded tracking page powered by PrettyDamnQuick Track360."
        matTooltipClass="pdq-mat-tooltip">
        @if (loadingPoweredByPdqMetrics) {
          <div class="metric-skeleton metric-skeleton--icon"></div>
          <div class="metric-skeleton metric-skeleton--label"></div>
          <div class="metric-skeleton metric-skeleton--value"></div>
        } @else {
          <div class="metric-icon performance-icon tracking-visits-icon">
            <i class="icon icon-tprg"></i>
          </div>
          <p class="metric-label">Tracking Page Visits</p>
          <p class="metric-value performance-value">{{ poweredByPdqMetrics.trackingPageVisits }}</p>
        }
      </div>
    </div>
  </div>

  <!-- Test Wins Section -->
  <div class="test-wins-section">
    <div class="section-header">
      <div class="section-title-area">
        <div class="section-title-row">
          <h2>
            Test Wins <span class="total-count">{{ testWinsTotalCount }} Total</span>
          </h2>
          @if (errorTestWins) {
            <button class="refresh-btn" (click)="onRefreshTestWins()" title="Refresh">
              <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
            </button>
          }
        </div>
      </div>
      <div class="section-actions">
        @if (hasAbTests) {
          <button class="view-all-btn" (click)="onViewAllTestWins()">
            View All <img src="assets/icons/icon-right-arrow.svg" alt="" class="view-all-icon" />
          </button>
        }
      </div>
    </div>

    @if (hasAbTests) {
      <!-- Test Win Cards Row -->
      <div class="test-wins-row">
        @for (testWin of displayedTestWins; track testWin.id) {
          <div class="test-win-card">
            @if (testWin.startedAt) {
              <span class="test-win-date">{{ testWin.startedAt }}</span>
            }
            <div class="test-win-title-group">
              <h3 class="test-win-name">{{ testWin.name }}</h3>
              <p class="test-win-segment">Implemented segment: {{ testWin.implementedSegment }}</p>
            </div>

            <div class="test-win-metrics">
              <div class="test-win-metric">
                <p class="metric-label">Revenue per Checkout</p>
                <p class="metric-value">
                  <span class="value">{{ testWin.revenuePerCheckout }}</span>
                  <span class="change positive">{{ testWin.revenuePerCheckoutChange }}</span>
                </p>
              </div>
              <div class="test-win-metric">
                <p class="metric-label">Annualized Impact</p>
                <p class="metric-value impact-value">{{ testWin.annualizedImpact }}</p>
              </div>
            </div>

            <button class="view-details-btn" (click)="onViewTestDetails(testWin)">View Details</button>
          </div>
        }
      </div>

      @if (remainingTestWinsCount > 0) {
        <button class="show-more-btn" (click)="onToggleShowAllTestWins()">
          @if (!showAllTestWins) {
            <img src="assets/icons/icon-down-arrow.svg" alt="" class="show-more-icon" />
          }
          {{ showAllTestWins ? 'Show Less' : 'Show ' + remainingTestWinsCount + ' More Wins' }}
        </button>
      }
    } @else {
      <!-- No AB Tests - Show Configure Card -->
      <div class="test-wins-row">
        <div class="test-win-card configure-card">
          <div class="metric-icon feature-icon abtest-icon">
            <i class="icon icon-abtest"></i>
          </div>
          <p class="feature-label">A/B Tests</p>
          <button class="configure-btn" (click)="onConfigureAbTests()">+ Configure</button>
        </div>
      </div>
    }
  </div>

  <!-- Strategy Catalog Section -->
  @if (isMaxxEnabled) {
    <div class="strategy-catalog-section test-wins-section">
      <div class="section-header">
        <div class="section-title-area">
          <div class="section-title-row">
            <h2>Strategy Catalog</h2>
            @if (errorStrategies) {
              <button class="refresh-btn" (click)="onRefreshStrategies()" title="Refresh">
                <img src="assets/icons/refresh.svg" alt="Refresh" class="refresh-icon" />
              </button>
            }
          </div>
        </div>
        <div class="section-actions">
          @if (strategyItemsRecommendedByMaxx.length > 0) {
            <button class="view-all-btn" routerLink="/optimize/strategy-catalog">
              View All <img src="assets/icons/icon-right-arrow.svg" alt="" class="view-all-icon" />
            </button>
          }
        </div>
      </div>

      @if (loadingStrategies) {
        <skeleton-strategy-catalog [numOfCards]="3"></skeleton-strategy-catalog>
      } @else if (strategyItemsRecommendedByMaxx.length > 0) {
        <div class="strategy-cards-row">
          @for (item of strategyItemsRecommendedByMaxx; track item[StrategyColumns.ID]) {
            <div class="strategy-card maxx-recommended-body" (click)="navigateToStrategyDetails(item)">
              <div class="card-header">
                <div class="category-header">
                  <div class="maxx-recommended">
                    <pdq-icon icon="pdqiq" size="small" color="raichu"></pdq-icon>
                    <span>MAXX Recommended for you</span>
                  </div>
                  <p class="strategy-category" [ngClass]="optimizeCategoryName(item)">
                    {{ item[StrategyColumns.PRIMARY_CATEGORY] }}
                  </p>
                </div>
                <h4 class="maxx-recommended-header">{{ item[StrategyColumns.NAME] }}</h4>
              </div>
              <div class="card-content">
                <p>{{ item[StrategyColumns.CONCEPT] }}</p>
              </div>
              <div class="kpi">
                <p class="title-text">Main KPIs:</p>
                <div class="kpi-maxx">
                  <div class="kpi-list">
                    @for (kpi of getKpiTargetsList(item); track $index) {
                      <p>{{ kpi }}</p>
                    }
                  </div>
                  <div class="enter-card">
                    <pdq-button [plain]="true" [underline]="true" variant="info">Show More</pdq-button>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <!-- No strategies available - Show placeholder -->
        <div class="strategy-cards-row">
          <div class="strategy-card empty-state">
            <div class="empty-icon">
              <pdq-icon icon="strategy" size="large" color="secondary"></pdq-icon>
            </div>
            <h4 class="empty-title">Strategy Recommendations</h4>
            <p class="empty-text">Data not available</p>
            <button class="configure-btn" routerLink="/optimize/strategy-catalog">Browse Strategies</button>
          </div>
        </div>
      }
    </div>
  }
</div>
